import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Input, List, Modal, Spin } from "antd";
import { fetchHealthUslTemplateItems, fetchHealthUslTemplates } from "./health-clients-api";
import { pick, toNumber } from "./health-clients-utils";

const VID_PAN_LABELS = {
  0: "Не планировать",
  1: "Каждый день",
  2: "Через день",
  7: "Раз в неделю",
  12: "Через день (сквозное)",
  100: "По времени"
};

const normalizeZag = (row) => ({
  idShb: toNumber(pick(row, "idShb", "IdShb")),
  name: pick(row, "name", "Name") ?? ""
});

const normalizeCnt = (row) => ({
  key: `${toNumber(pick(row, "idShb", "IdShb"))}-${toNumber(pick(row, "idStr", "IdStr"))}`,
  idUsl: toNumber(pick(row, "idUsl", "IdUsl")),
  tovName: pick(row, "tovName", "TovName") ?? "",
  cnt: toNumber(pick(row, "cnt", "Cnt")),
  vidPlan: toNumber(pick(row, "vidPlan", "VidPlan")),
  isPlat: toNumber(pick(row, "isPlat", "IsPlat"))
});

/**
 * Выбор шаблона назначений (Delphi TFrmSprShbNazn / AShbSpis).
 */
const ClientAppointmentTemplateModal = ({ open, onCancel, onSelect }) => {
  const [templates, setTemplates] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [nameFilter, setNameFilter] = useState("");
  const [loadingList, setLoadingList] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [error, setError] = useState("");
  const listReqRef = useRef(0);
  const itemsReqRef = useRef(0);

  useEffect(() => {
    if (!open) return;

    const requestId = ++listReqRef.current;
    setLoadingList(true);
    setError("");
    setTemplates([]);
    setItems([]);
    setSelectedId(null);
    setNameFilter("");

    fetchHealthUslTemplates()
      .then((data) => {
        if (requestId !== listReqRef.current) return;
        const rows = Array.isArray(data) ? data.map(normalizeZag).filter((r) => r.idShb != null) : [];
        setTemplates(rows);
        if (rows.length) setSelectedId(rows[0].idShb);
      })
      .catch((e) => {
        if (requestId !== listReqRef.current) return;
        setError(e.message || "Не удалось загрузить шаблоны");
      })
      .finally(() => {
        if (requestId === listReqRef.current) setLoadingList(false);
      });
  }, [open]);

  useEffect(() => {
    if (!open || selectedId == null) {
      setItems([]);
      return;
    }

    const requestId = ++itemsReqRef.current;
    setLoadingItems(true);
    fetchHealthUslTemplateItems(selectedId)
      .then((data) => {
        if (requestId !== itemsReqRef.current) return;
        setItems(Array.isArray(data) ? data.map(normalizeCnt) : []);
      })
      .catch(() => {
        if (requestId !== itemsReqRef.current) return;
        setItems([]);
      })
      .finally(() => {
        if (requestId === itemsReqRef.current) setLoadingItems(false);
      });
  }, [open, selectedId]);

  const filteredTemplates = useMemo(() => {
    const q = nameFilter.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) => (t.name || "").toLowerCase().includes(q));
  }, [templates, nameFilter]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.idShb === selectedId) || null,
    [templates, selectedId]
  );

  useEffect(() => {
    if (!open) return;
    if (!filteredTemplates.length) {
      setSelectedId(null);
      return;
    }
    if (selectedId != null && filteredTemplates.some((t) => t.idShb === selectedId)) return;
    setSelectedId(filteredTemplates[0].idShb);
  }, [open, filteredTemplates, selectedId]);

  return (
    <Modal
      title="Добавить из шаблона"
      open={open}
      onCancel={onCancel}
      onOk={() => {
        if (selectedId == null || !items.length) return false;
        return onSelect?.(selectedId, selectedTemplate);
      }}
      okText="ОК"
      cancelText="Отмена"
      okButtonProps={{ disabled: selectedId == null || !items.length }}
      destroyOnHidden
      width={832}
    >
      {error ? (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />
      ) : null}

      <Input
        allowClear
        size="small"
        placeholder="Фильтр по имени шаблона"
        value={nameFilter}
        onChange={(e) => setNameFilter(e.target.value)}
        className="client-appointment-template__filter"
      />

      <div className="client-appointment-template">
        <div className="client-appointment-template__list">
          <div className="client-appointment-template__caption">Шаблоны</div>
          {loadingList ? (
            <div className="client-appointment-template__center">
              <Spin size="small" />
            </div>
          ) : filteredTemplates.length ? (
            <List
              size="small"
              bordered
              dataSource={filteredTemplates}
              renderItem={(item) => (
                <List.Item
                  className={
                    item.idShb === selectedId
                      ? "client-appointment-template__item client-appointment-template__item--active"
                      : "client-appointment-template__item"
                  }
                  onClick={() => setSelectedId(item.idShb)}
                >
                  {item.name || `#${item.idShb}`}
                </List.Item>
              )}
            />
          ) : (
            <div className="client-appointment-template__empty">
              {templates.length ? "Ничего не найдено" : "Нет шаблонов"}
            </div>
          )}
        </div>

        <div className="client-appointment-template__detail">
          <div className="client-appointment-template__caption">Состав</div>
          {loadingItems ? (
            <div className="client-appointment-template__center">
              <Spin size="small" />
            </div>
          ) : items.length ? (
            <ul className="client-appointment-template__services">
              {items.map((item) => (
                <li key={item.key}>
                  <span className="client-appointment-template__svc-name">
                    {item.tovName || `#${item.idUsl}`}
                  </span>
                  <span className="client-appointment-template__svc-meta">
                    {item.cnt != null ? `×${item.cnt}` : ""}
                    {item.isPlat === 1 ? " · платно" : ""}
                    {VID_PAN_LABELS[item.vidPlan] ? ` · ${VID_PAN_LABELS[item.vidPlan]}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="client-appointment-template__empty">
              {selectedId == null ? "Выберите шаблон" : "Пустой шаблон"}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ClientAppointmentTemplateModal;
